/**
 * 318 (#633) — the schedule's row density: a per-viewer preference kept in this browser, like the
 * theme, and deliberately NOT in the address (316 made the address the shared state; a colleague
 * opening a link keeps their own density).
 *
 * 🔴 Every storage access is guarded: in a private window or with site data blocked the accessor
 * itself can throw, and the schedule must still load (FR-008).
 */

export type ScheduleDensity = 'comfortable' | 'compact';

export const DENSITY_STORAGE_KEY = 'carii-admin-schedule-density';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function readDensity(storage: StorageLike | undefined): ScheduleDensity {
  try {
    return storage?.getItem(DENSITY_STORAGE_KEY) === 'compact' ? 'compact' : 'comfortable';
  } catch {
    return 'comfortable';
  }
}

export function writeDensity(storage: StorageLike | undefined, density: ScheduleDensity): void {
  try {
    if (density === 'compact') storage?.setItem(DENSITY_STORAGE_KEY, 'compact');
    else storage?.removeItem(DENSITY_STORAGE_KEY);
  } catch {
    // The choice still applies for this visit; it simply is not remembered.
  }
}

/**
 * The geometry per density (research R1). 28 px lanes are what fits 20 cars in the 570 px the
 * frame leaves at 1440 × 900; literal strings so Tailwind's scanner sees each one.
 */
export function densityClasses(density: ScheduleDensity): { lane: string; row: string } {
  return density === 'compact'
    ? { lane: 'h-7', row: 'min-h-7' }
    : { lane: 'h-11', row: 'min-h-11' };
}

/**
 * A bar's vertical padding: 4 px, or 2 px inside a Compact timeline. Keyed on the timeline root's
 * `data-density` in CSS rather than passed as a prop, so a switch does not change any bar's props
 * and the 288 memoised bars of a full Month are not re-rendered (SC-004).
 */
export const BAR_PADDING = 'py-1 [[data-density=compact]_&]:py-0.5';
